import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import VideoCard from "@/components/feature/VideoCard";
import DemoPlayer from "@/pages/detail/components/DemoPlayer";
import EpisodeList from "@/pages/detail/components/EpisodeList";
import DetailActions from "@/pages/detail/components/DetailActions";
import {
  formatUpdated,
  getCategoryMeta,
  getEpisodeLabel,
  getRelatedVideos,
  getVideoById,
} from "@/lib/videoData";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function DetailPage() {
  const { id } = useParams();
  const video = getVideoById(id);
  const [episode, setEpisode] = useState(1);

  useEffect(() => {
    setEpisode(1);
  }, [id]);

  usePageMeta(
    video ? `${video.title} - 影视详情 - 糖豆视频` : "视频未找到 - 糖豆视频",
    video
      ? `${video.description.slice(0, 90)} 类型：${video.genre.join("、")}，主演：${video.actors.join("、")}，导演：${video.director}，${video.year} 年${video.region}作品。`
      : "该视频内容不存在或已下线，请返回糖豆视频首页浏览精选影视综艺。",
    video ? `${video.title},${video.genre.join(",")},糖豆视频,影视详情,${video.year}${video.region}影视` : "糖豆视频",
  );

  if (!video) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-100">
        <Navbar />
        <main className="mx-auto flex w-full max-w-[1360px] flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-50 text-primary-600">
            <i className="ri-file-unknow-line text-3xl" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">视频不存在</h1>
          <p className="mt-2 text-sm text-foreground-600">链接可能已失效，或者该内容已从演示片库中移除。</p>
          <Link
            to="/"
            className="mt-6 flex h-10 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-800 px-5 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-900"
          >
            <i className="ri-home-4-line text-sm" />
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const category = getCategoryMeta(video.category);
  const related = getRelatedVideos(video, 6);
  const episodeLabel = getEpisodeLabel(video);

  const infoRows = [
    { label: "类型", value: video.genre.join(" / ") },
    { label: "主演", value: video.actors.join(" / ") },
    { label: "导演", value: video.director },
    { label: "年份", value: `${video.year} 年` },
    { label: "地区", value: video.region },
    ...(video.episodeCount > 0
      ? [
          { label: "单集时长", value: video.duration },
          { label: "集数", value: `共 ${video.episodeCount} ${episodeLabel}` },
        ]
      : [{ label: "片长", value: video.duration }]),
    { label: "更新时间", value: formatUpdated(video.updatedAt) },
  ];

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-100">
      <Navbar />

      <main className="w-full flex-1">
        <section className="relative w-full overflow-hidden bg-primary-950">
          <img
            src={video.cover}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full scale-110 object-cover object-top opacity-25 blur-md"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/92 to-primary-950/70" />

          <div className="relative mx-auto w-full max-w-[1360px] px-4 pb-8 pt-5 md:px-8 md:pb-12 md:pt-6">
            <nav className="flex flex-wrap items-center gap-1.5 text-xs text-background-400" aria-label="面包屑导航">
              <Link to="/" className="transition-colors hover:text-accent-300">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-sm" />
              <Link to={`/category/${video.category}`} className="transition-colors hover:text-accent-300">
                {category?.name}
              </Link>
              <i className="ri-arrow-right-s-line text-sm" />
              <span className="line-clamp-1 max-w-[200px] font-medium text-background-200">{video.title}</span>
            </nav>

            <div className="mt-5 flex flex-col gap-6 md:flex-row md:gap-8">
              <div className="relative w-[136px] shrink-0 overflow-hidden rounded-lg bg-primary-900 ring-1 ring-background-50/10 sm:w-[168px] md:w-[220px]">
                <div className="aspect-[2/3] w-full">
                  <img
                    src={video.cover}
                    alt={`${video.title} 封面`}
                    title={`${video.title} - 影视详情 - 糖豆视频`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <span className="absolute right-2 top-2 flex items-center gap-1 rounded bg-accent-500 px-1.5 py-0.5 text-[11px] font-bold text-foreground-950">
                  <i className="ri-star-fill text-[10px]" />
                  {video.rating.toFixed(1)}
                </span>
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to={`/category/${video.category}`}
                    className="rounded-full bg-accent-500/15 px-3 py-1 text-[11px] font-semibold text-accent-300 transition-colors hover:bg-accent-500/25"
                  >
                    {category?.name}
                  </Link>
                  <span className="rounded-full border border-background-50/20 px-3 py-1 text-[11px] font-medium text-background-200">
                    {video.year}
                  </span>
                  <span className="rounded-full border border-background-50/20 px-3 py-1 text-[11px] font-medium text-background-200">
                    {video.region}
                  </span>
                  <span className="flex items-center gap-1 rounded-full bg-primary-800 px-3 py-1 text-[11px] font-medium text-accent-300">
                    <i className="ri-fire-line text-xs" />
                    {(video.heat / 1000).toFixed(1)} 万热度
                  </span>
                </div>

                <h1 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-background-50 md:text-[42px]">
                  {video.title}
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-300">
                  {video.tagline}
                </p>

                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-background-200">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-star-line text-sm text-accent-400" />
                    主演：{video.actors.join(" / ")}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-clapperboard-line text-sm text-accent-400" />
                    导演：{video.director}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line text-sm text-accent-400" />
                    {video.duration}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line text-sm text-accent-400" />
                    更新于 {video.updatedAt}
                  </span>
                </div>

                <div className="mt-3 inline-flex items-center gap-2 rounded-md bg-primary-900/70 px-3 py-1.5 text-[11px] text-background-200">
                  <i className="ri-information-line text-sm text-accent-400" />
                  {video.status} · 内容信息为演示示例
                </div>

                <DetailActions videoId={video.id} title={video.title} />
              </div>
            </div>
          </div>
        </section>

        <div className="mx-auto w-full max-w-[1360px] px-4 py-7 md:px-8 md:py-9">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px] lg:gap-7">
            <div className="space-y-5 md:space-y-6">
              <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                    <i className="ri-play-circle-line text-lg text-primary-600" />
                    预览播放
                  </h2>
                  <span className="text-[11px] text-foreground-500">演示模式 · 未接入真实片源</span>
                </div>
                <DemoPlayer video={video} episode={episode} />
              </section>

              <EpisodeList video={video} current={episode} onChange={setEpisode} />

              <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-file-text-line text-lg text-primary-600" />
                  剧情简介
                </h2>
                <p className="mt-3 text-sm leading-7 text-foreground-700">{video.description}</p>
                <p className="mt-3 text-sm leading-7 text-foreground-600">
                  《{video.title}》是 {video.year} 年出品的{video.region}{category?.name}作品，由 {video.director} 执导，
                  {video.actors.join("、")} 主演，全片{video.duration}。本页信息与封面均来自糖豆视频演示片库，仅用于展示内容组织方式。
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {video.genre.map((tag) => (
                    <Link
                      key={tag}
                      to={`/search?q=${encodeURIComponent(tag)}`}
                      className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-[11px] font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </section>
            </div>

            <aside className="space-y-5">
              <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-information-line text-lg text-primary-600" />
                  影片信息
                </h2>
                <dl className="mt-3 divide-y divide-background-200/80">
                  {infoRows.map((row) => (
                    <div key={row.label} className="flex items-start gap-3 py-2.5">
                      <dt className="w-16 shrink-0 text-xs text-foreground-500">{row.label}</dt>
                      <dd className="min-w-0 flex-1 text-xs leading-relaxed text-foreground-800">{row.value}</dd>
                    </div>
                  ))}
                </dl>
                <div className="mt-3 flex items-center justify-between rounded-md bg-background-100 px-3 py-2.5">
                  <span className="text-xs text-foreground-600">豆瓣风格评分</span>
                  <span className="flex items-center gap-1 font-heading text-lg font-extrabold text-accent-700">
                    <i className="ri-star-fill text-sm" />
                    {video.rating.toFixed(1)}
                  </span>
                </div>
              </section>

              <section className="rounded-lg border border-primary-200 bg-primary-50 p-4 md:p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-primary-900">
                  <i className="ri-folder-open-line text-lg text-primary-700" />
                  所属分类
                </h2>
                <div className="mt-3 space-y-2">
                  <Link
                    to={`/category/${video.category}`}
                    className="flex items-center justify-between rounded-md bg-background-50 px-3 py-2.5 text-xs font-medium text-primary-800 transition-colors hover:bg-background-100"
                  >
                    {category?.name}频道
                    <i className="ri-arrow-right-s-line text-base" />
                  </Link>
                  <Link
                    to={`/search?q=${encodeURIComponent(video.genre[0])}`}
                    className="flex items-center justify-between rounded-md bg-background-50 px-3 py-2.5 text-xs font-medium text-primary-800 transition-colors hover:bg-background-100"
                  >
                    {video.genre[0]}题材
                    <i className="ri-arrow-right-s-line text-base" />
                  </Link>
                </div>
                <p className="mt-3 text-[11px] leading-relaxed text-primary-800/80">
                  想看更多同类内容？进入分类页可以按地区、年份与热度继续筛选。
                </p>
              </section>

              <section className="rounded-lg bg-primary-950 p-4 md:p-5">
                <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-background-50">
                  <i className="ri-customer-service-2-line text-base text-accent-300" />
                  内容反馈
                </h2>
                <p className="mt-2 text-[11px] leading-relaxed text-background-300">
                  发现信息有误或想推荐影片，欢迎通过关于页的邮箱联系我们，我们会在两个工作日内回复。
                </p>
                <Link
                  to="/about"
                  className="mt-3 flex h-9 items-center justify-center gap-1.5 rounded-md border border-background-50/25 text-[11px] font-medium text-background-100 transition-colors hover:border-accent-400 hover:text-accent-300"
                >
                  前往关于我们
                  <i className="ri-arrow-right-line text-sm" />
                </Link>
              </section>
            </aside>
          </div>

          <section className="mt-8 border-t border-background-200 pt-7 md:mt-10">
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="font-heading text-xl font-bold tracking-tight text-foreground-950">
                  你可能还想看
                </h2>
                <p className="mt-1 text-xs text-foreground-600">根据相同分类、题材与演员自动推荐的演示内容</p>
              </div>
              <Link
                to={`/category/${video.category}`}
                className="flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium text-foreground-700 transition-colors hover:bg-background-200/60 hover:text-primary-700"
              >
                更多{category?.name}
                <i className="ri-arrow-right-line text-sm" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-3 sm:gap-4 lg:grid-cols-6">
              {related.map((item) => (
                <VideoCard key={item.id} video={item} showTagline={false} />
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}